import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Alert,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { ISymbol } from '../../common/components/dialogs/symbolSelectDialog/SymbolSelect'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

interface TradesTableProps {
  backtest?: any
  alertId?: string
  selectedGroup: string
}

interface TradeData {
  id: string
  entryTime: number
  exitTime?: number
  profit: number
  commission: number
  status: string
  group: string
  type: string
  entryPrice: number
  exitPrice?: number
  tp: number
  sl: number
  lotSize: number
  openEquity: number
  priceOfFractal?: number
  tpTime?: number
}

interface ColumnData {
  dataKey: keyof TradeData | string
  label: string
  width: number
  align?: 'left' | 'center' | 'right'
  type?: 'string' | 'currency' | 'number' | 'date' | 'duration'
  color?: 'profit' | 'type'
  conditional?: boolean // For columns that may not always be shown
}

const columns: ColumnData[] = [
  { dataKey: 'entryTime', label: 'Entry Time', width: 140, type: 'date' },
  { dataKey: 'dayOfWeek', label: 'Day of Week', width: 100, type: 'string' },
  { dataKey: 'exitTime', label: 'Exit Time', width: 140, type: 'date' },
  { dataKey: 'duration', label: 'Duration', width: 120, type: 'duration' },
  { dataKey: 'profit', label: 'Profit', width: 100, type: 'currency', color: 'profit' },
  { dataKey: 'commission', label: 'Commission', width: 100, type: 'currency' },
  { dataKey: 'openEquity', label: 'Open Equity', width: 100, type: 'currency' },
  { dataKey: 'tp', label: 'Take Profit', width: 100, type: 'number' },
  { dataKey: 'sl', label: 'Stop Loss', width: 100, type: 'number' },
  { dataKey: 'lotSize', label: 'Lot Size', width: 80, type: 'number' },
  { dataKey: 'type', label: 'Type', width: 80, type: 'string', color: 'type' },
  { dataKey: 'entryPrice', label: 'Entry Price', width: 100, type: 'number' },
  { dataKey: 'exitPrice', label: 'Exit Price', width: 100, type: 'number' },
  {
    dataKey: 'priceOfFractal',
    label: 'Fractal Price',
    width: 100,
    type: 'number',
    conditional: true,
  },
  { dataKey: 'tpTime', label: 'Dynamic TP Time', width: 140, type: 'date', conditional: true },
]

const VirtuosoTableComponents: any = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  TableRow: ({ item: _item, ...props }: any) => <TableRow {...props} />,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
}

function FixedHeaderContent({
  hasFractalPrice,
  hasTpTime,
}: {
  hasFractalPrice: boolean
  hasTpTime: boolean
}) {
  const theme = useTheme()

  return (
    <TableRow
      sx={{
        background: theme.palette.background.custom2,
      }}
    >
      {columns
        .filter((column) => {
          if (column.conditional) {
            if (column.dataKey === 'priceOfFractal') return hasFractalPrice
            if (column.dataKey === 'tpTime') return hasTpTime
          }
          return true
        })
        .map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.align || 'left'}
            style={{ width: column.width }}
            sx={{ backgroundColor: theme.palette.background.custom2 }}
          >
            {column.label}
          </TableCell>
        ))}
    </TableRow>
  )
}

function RowContent({
  trade,
  symbolObject,
  backtestSettings,
  hasFractalPrice,
  hasTpTime,
}: {
  trade: TradeData
  symbolObject?: ISymbol
  backtestSettings: any
  hasFractalPrice: boolean
  hasTpTime: boolean
}) {
  const theme = useTheme()

  const getSymbolDecimals = (symbolObject?: ISymbol): number => {
    if (!symbolObject) return 2

    if (symbolObject.params && symbolObject.params.decimals) {
      return symbolObject.params.decimals
    }

    if (symbolObject.type === 'forex') {
      if (symbolObject.name.endsWith('JPY')) {
        return 3
      } else {
        return 5
      }
    }

    return 2
  }

  const getTradeDuration = (tradeDoc: TradeData): string => {
    if (!tradeDoc.exitTime || !tradeDoc.entryTime) {
      return ''
    }

    let diffSecs = Math.floor((tradeDoc.exitTime - tradeDoc.entryTime) / 1000)
    let diff = ''

    if (diffSecs >= 3600) {
      const hours = Math.floor(diffSecs / 3600)
      diff += `${hours}hr`
      diffSecs %= 3600
    }

    if (diffSecs >= 60) {
      const minutes = Math.floor(diffSecs / 60)
      diff += ` ${minutes}m`
      diffSecs %= 60
    }

    diff += ` ${diffSecs}s`
    return diff.trim()
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-us', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatData = (value: any, column: ColumnData): string => {
    switch (column.type) {
      case 'currency':
        return formatCurrency(value || 0)
      case 'date':
        if (!value) return column.dataKey === 'exitTime' ? 'Open Trade' : ''
        return dayjs(value).format('MM/DD/YYYY HH:mm')
      case 'duration':
        return getTradeDuration(trade)
      case 'number':
        const decimals = getSymbolDecimals(symbolObject)
        return value ? value.toFixed(decimals) : ''
      default:
        return value || ''
    }
  }

  const isClosed = trade.status === 'CLOSED' || trade.status === 'closed'
  const profitColor = trade.profit >= 0 ? theme.palette.success.main : theme.palette.error.main

  let lotSize = trade.lotSize || 0
  if (symbolObject?.type === 'forex') {
    lotSize /= backtestSettings.analyzeTrades.contractSize
  }

  // Create enhanced trade object with computed values
  const enhancedTrade = {
    ...trade,
    dayOfWeek: dayjs(trade.entryTime).format('dddd'),
    duration: getTradeDuration(trade),
    lotSize: lotSize,
  }

  return (
    <React.Fragment>
      {columns
        .filter((column) => {
          if (column.conditional) {
            if (column.dataKey === 'priceOfFractal') return hasFractalPrice
            if (column.dataKey === 'tpTime') return hasTpTime
          }
          return true
        })
        .map((column) => {
          const value = enhancedTrade[column.dataKey as keyof typeof enhancedTrade]
          let displayValue = formatData(value, column)

          // Handle special cases
          if (column.dataKey === 'profit' && !isClosed) {
            displayValue = ''
          }

          return (
            <TableCell
              key={column.dataKey}
              align={column.align || 'left'}
              style={{ width: column.width }}
              sx={{
                py: 1,
                px: 1,
                backgroundColor: 'inherit',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color:
                    column.color === 'profit'
                      ? profitColor
                      : column.color === 'type'
                        ? displayValue === 'buy'
                          ? theme.palette.success.main
                          : theme.palette.error.main
                        : 'inherit',
                }}
              >
                {displayValue}
              </Typography>
            </TableCell>
          )
        })}
    </React.Fragment>
  )
}

const TradesTable: React.FC<TradesTableProps> = ({ backtest, alertId, selectedGroup }) => {
  const theme = useTheme()
  const { symbols } = useApplicationSuiteContext()

  const [trades, setTrades] = useState<TradeData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [getBacktestTrades] = useHttpsCallable(firebase_functions, 'getBacktestTrades')

  // Fetch trades for selected group
  useEffect(() => {
    if (!backtest?.id && !alertId) return
    if (!selectedGroup) {
      setTrades([])
      return
    }

    const fetchTrades = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const result: any = await getBacktestTrades({
          backtestId: backtest?.id,
          alertId,
          tradeGroup: selectedGroup,
        })

        if (result?.data?.data) {
          setTrades(result.data.data)
        } else {
          setTrades([])
        }
      } catch (err) {
        console.error('Error fetching trades:', err)
        setError('Failed to load trades. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrades()
  }, [backtest.id, alertId, selectedGroup, getBacktestTrades])

  const symbolObject = useMemo(() => {
    if (!backtest?.backtestSettings?.executionSymbol) return undefined
    return symbols?.find(
      (symbolDoc) => symbolDoc.name === backtest?.backtestSettings.executionSymbol
    )
  }, [backtest?.backtestSettings?.executionSymbol, symbols])

  const sortedTrades = useMemo(() => {
    return [...trades].sort((a, b) => {
      if (a.group > b.group) return 1
      if (a.group < b.group) return -1
      if (a.group === b.group) {
        return new Date(a.entryTime).getTime() > new Date(b.entryTime).getTime() ? 1 : -1
      }
      return -1
    })
  }, [trades])

  const hasFractalPrice = trades.some((trade) => trade.priceOfFractal)
  const hasTpTime = trades.some((trade) => trade.tpTime)

  const tradeStats = useMemo(() => {
    const closedTrades = sortedTrades.filter(
      (trade) => trade.status === 'CLOSED' || trade.status === 'closed'
    )
    const openTrades = sortedTrades.filter(
      (trade) => trade.status !== 'CLOSED' && trade.status !== 'closed'
    )
    const buyTrades = sortedTrades.filter((trade) => trade.type === 'buy')
    const sellTrades = sortedTrades.filter((trade) => trade.type === 'sell')

    return {
      total: sortedTrades.length,
      closed: closedTrades.length,
      open: openTrades.length,
      buy: buyTrades.length,
      sell: sellTrades.length,
    }
  }, [sortedTrades])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Box textAlign="center">
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading trades...
          </Typography>
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    )
  }

  if (sortedTrades.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography variant="h6" color="text.secondary">
          No trades found for the selected group
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Total Trades
            </Typography>
            <Typography variant="h6">{tradeStats.total}</Typography>
          </Box>

          {tradeStats.open > 0 && (
            <>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Closed Trades
                </Typography>
                <Typography variant="h6">{tradeStats.closed}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Open Trades
                </Typography>
                <Typography variant="h6">{tradeStats.open}</Typography>
              </Box>
            </>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Buy Trades
            </Typography>
            <Typography variant="h6">{tradeStats.buy}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Sell Trades
            </Typography>
            <Typography variant="h6">{tradeStats.sell}</Typography>
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={0}
        style={{ height: 400, width: '100%', position: 'relative', background: 'none' }}
      >
        <Box
          sx={{
            '& .MuiTable-root': {
              borderSpacing: '0 2px !important',
            },
            '& .MuiTableCell-root': {
              padding: '4px 1px',
            },
          }}
        >
          <TableVirtuoso
            style={{ height: 400, width: '100%', background: theme.palette.background.custom2 }}
            data={sortedTrades}
            components={VirtuosoTableComponents}
            fixedHeaderContent={() => (
              <FixedHeaderContent hasFractalPrice={hasFractalPrice} hasTpTime={hasTpTime} />
            )}
            itemContent={(index, trade) => (
              <RowContent
                trade={trade}
                symbolObject={symbolObject}
                backtestSettings={backtest?.backtestSettings}
                hasFractalPrice={hasFractalPrice}
                hasTpTime={hasTpTime}
              />
            )}
          />
        </Box>
      </Paper>
    </>
  )
}

export default TradesTable
